/**
 * Turnstile (explicit rendering)
 *
 * トークンは単回使用・約300秒で失効するため、送信ごとに reset() して取り直す。
 */

const ACTION = 'register'
const READY_TIMEOUT_MS = 15000
const TOKEN_TIMEOUT_MS = 20000

type RenderOptions = {
  sitekey: string
  action: string
  appearance: string
  size: string
  callback: (token: string) => void
  'error-callback': () => void
  'expired-callback': () => void
}

type TurnstileApi = {
  render(element: HTMLElement, options: RenderOptions): string | undefined
  reset(widgetId: string): void
  remove(widgetId: string): void
  getResponse(widgetId: string): string | undefined
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

/** api.js は async 読み込みのため、window.turnstile が生えるまで待つ */
function waitForApi(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  return new Promise((resolve, reject) => {
    const started = Date.now()
    const timer = setInterval(() => {
      if (window.turnstile) {
        clearInterval(timer)
        resolve(window.turnstile)
      } else if (Date.now() - started > READY_TIMEOUT_MS) {
        clearInterval(timer)
        reject(new Error('turnstile-unavailable'))
      }
    }, 100)
  })
}

/** 1つのウィジェットを保持し、送信のたびにトークンを供給する */
export class TurnstileField {
  private widgetId: string | null = null
  private token: string | null = null
  private waiters: { resolve: (token: string) => void; reject: (err: Error) => void }[] = []
  private failed = false

  async mount(element: HTMLElement, siteKey: string): Promise<void> {
    if (this.widgetId !== null) return
    const api = await waitForApi()
    const widgetId = api.render(element, {
      sitekey: siteKey,
      action: ACTION,
      appearance: 'interaction-only',
      size: 'flexible',
      callback: (token: string) => {
        this.failed = false
        this.token = token
        for (const waiter of this.waiters.splice(0)) waiter.resolve(token)
      },
      'error-callback': () => {
        this.failed = true
        this.token = null
        for (const waiter of this.waiters.splice(0)) {
          waiter.reject(new Error('turnstile-error'))
        }
      },
      'expired-callback': () => {
        this.token = null
        this.reset()
      },
    })
    this.widgetId = widgetId ?? null
  }

  /** トークンを取得する。まだ発行されていなければ待つ */
  getToken(): Promise<string> {
    if (this.token) return Promise.resolve(this.token)
    if (this.failed) return Promise.reject(new Error('turnstile-error'))
    return new Promise((resolve, reject) => {
      const waiter = {
        resolve,
        reject,
      }
      this.waiters.push(waiter)
      setTimeout(() => {
        const index = this.waiters.indexOf(waiter)
        if (index >= 0) {
          this.waiters.splice(index, 1)
          reject(new Error('turnstile-timeout'))
        }
      }, TOKEN_TIMEOUT_MS)
    })
  }

  /** 送信後にトークンを取り直す */
  reset(): void {
    this.token = null
    if (this.widgetId !== null) window.turnstile?.reset(this.widgetId)
  }

  destroy(): void {
    if (this.widgetId !== null) {
      window.turnstile?.remove(this.widgetId)
      this.widgetId = null
    }
    this.token = null
  }
}
